import { FC, useContext, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Modal,
  BackwardIcon,
  Preloader,
  H3,
  Select,
  TDropdownOptionValue,
  AlienIcon,
  H2,
  GhostIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { HostsModalsDataResponse } from 'components';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { getRoleIcon } from 'utils';

import s from './ChangeHostModal.module.scss';
import {
  CHANGE_HOST,
  CHANGE_HOSTS_DATA,
  IChangeHostResponse,
  IHostGroupsResponse,
  FREE_HOSTS,
  FreeHostsRequest,
  FreeHostsResponse,
} from './schema';
import { updateChangeHostCache } from './updateChangeHostCache';

interface IManageHostModalProps {
  hosts?: HostsModalsDataResponse;
  hostId?: number;
  closeModal: () => void;
}

export const ChangeHostModal: FC<IManageHostModalProps> = ({
  hosts,
  hostId,
  closeModal,
}) => {
  const { t } = useContext(LocalizationContext);

  const { data: freeHostsData, loading: freeHostsLoading } = useQuery<
    FreeHostsResponse,
    FreeHostsRequest
  >(FREE_HOSTS, {
    skip: !hostId,
    variables: {
      host_id: hostId || 0,
    },
    fetchPolicy: 'no-cache',
  });

  const { data, loading: dataLoading } = useQuery<IHostGroupsResponse>(
    CHANGE_HOSTS_DATA,
    {
      fetchPolicy: 'cache-only',
    }
  );

  const GROUP_OPTIONS =
    freeHostsData?.freeHosts
      ?.map((host) => ({
        value: host.group.id,
        title: host.group.title,
      }))
      .filter(
        (group, index, self) =>
          index === self.findIndex((g) => g.value === group.value)
      ) || [];

  const currentHost = hosts?.hostsOverview?.find((host) => host.id === hostId);

  const currentGroupTitle = data?.hostGroupsOverview.find(
    (group) => group.id === currentHost?.group_id
  )?.title;
  const currentGroupPriority = data?.hostGroupsOverview.find(
    (group) => group.id === currentHost?.group_id
  )?.priority;

  const {
    alias,
    client_sessions: [activeSession],
  } = currentHost ?? { client_sessions: [] };

  const [selectedHostId, setSelectedHostId] = useState<number | null>(null);
  const [selectedGroup, setSelectedGroup] =
    useState<TDropdownOptionValue | null>(null);

  function unicodeCompare(a: string, b: string): number {
    return a < b ? -1 : a > b ? 1 : 0;
  }

  const freeHosts = useMemo(() => {
    if (!freeHostsData?.freeHosts) return [];

    const currentHostGroupId = currentHost?.group_id;

    const sortedHosts = [...freeHostsData.freeHosts].sort((a, b) => {
      if (currentHostGroupId) {
        const aIsCurrentGroup = a.group.id === currentHostGroupId;
        const bIsCurrentGroup = b.group.id === currentHostGroupId;

        if (aIsCurrentGroup && !bIsCurrentGroup) return -1;
        if (!aIsCurrentGroup && bIsCurrentGroup) return 1;
      }

      const groupCompare = unicodeCompare(a.group.title, b.group.title);
      if (groupCompare !== 0) return groupCompare;

      return unicodeCompare(a.alias, b.alias);
    });

    return selectedGroup
      ? sortedHosts.filter((host) => host.group.id === selectedGroup)
      : sortedHosts;
  }, [freeHostsData, selectedGroup, currentHost?.group_id]);

  const selectedHost = freeHosts?.find((host) => host.id === selectedHostId);

  const [changeHost, { loading: isHostChanging }] =
    useMutation<IChangeHostResponse>(CHANGE_HOST, {
      update: updateChangeHostCache,
      onCompleted: () => {
        closeModal();
        notification.info({
          t,
          leftDescription: selectedHost?.alias,
          title: t('session.changeHost.success'),
          type: 'success',
          dismiss: 5,
        });
      },
      onError: (error) => {
        notification.info({
          t,
          leftDescription: selectedHost?.alias,
          title: t('change.host.error'),
          message: error.message,
          type: 'error',
          dismiss: 5,
        });
      },
    });

  return (
    <Modal onClose={closeModal} className={s.container}>
      <H2>{t('session.changeHost.title')}</H2>
      <div className={s.content}>
        <div className={s.header}>
          {currentHost?.user ? (
            <div className={s.user_block}>
              <div className={s.user}>
                <div className={s.user_avatar}>
                  {currentHost?.user?.avatar_url ? (
                    <img
                      className={s.user_image}
                      src={currentHost?.user?.avatar_url}
                      alt="avatar"
                    />
                  ) : (
                    <AlienIcon className={s.alien_icon} color="disable" />
                  )}
                  {currentHost?.user?.roles?.length !== 0 && (
                    <div className={s.user_role}>
                      {getRoleIcon(currentHost?.user?.roles || [])}
                    </div>
                  )}
                </div>
                <div className={s.user_info}>
                  <H2>
                    {currentHost?.user?.nickname ?? currentHost?.user?.phone}
                  </H2>
                  <span className={s.phone}>{currentHost?.user?.phone}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className={s.user_block}>
              <div className={s.user}>
                <div className={s.user_avatar}>
                  <GhostIcon color="primary" size="m" />
                </div>
                <div className={s.user_info}>
                  <H2>{t('client.guest')}</H2>
                </div>
              </div>
            </div>
          )}
          <div className={s['host-changer']}>
            <div className={s['host-info']}>
              <div className={s.info_name}>
                <div className={s.text}>{alias}</div>
                <div className={s['group-name-top']}>{currentGroupTitle}</div>
              </div>
              <div className={s.info_priority}>
                {t('priority')} | {currentGroupPriority ?? ''}
              </div>
            </div>
            <BackwardIcon className={s.icon} color="secondary" size="s" />
            <div
              className={cn(s['host-info'], { [s['empty']]: !selectedHost })}
            >
              {selectedHost ? (
                <>
                  <div className={s.info_name}>
                    <div className={s.text}>{selectedHost.alias}</div>
                    <div className={s['group-name-top']}>
                      {selectedHost?.group?.title}
                    </div>
                  </div>
                  <div className={s.info_priority}>
                    {t('priority')} | {currentGroupPriority ?? ''}
                  </div>
                </>
              ) : (
                <div className={s.empty_block}>
                  {t('session.changeHost.selected.placeholder')}
                </div>
              )}
            </div>
          </div>
        </div>
        <Preloader
          loading={dataLoading || freeHostsLoading}
          className={s.preloader}
        >
          <div className={s.chosen_block}>
            <H3 className={s.title}>{t('session.changeHost.available')}</H3>
            <Select
              name="filter"
              options={GROUP_OPTIONS}
              value={selectedGroup}
              onChange={(value) => {
                setSelectedGroup(value);
              }}
              placeholder={t('groups.all')}
              disabled={!freeHosts.length}
              errorWrapperClassName={cn(selectedGroup && s.select)}
              renderOption={(item) => (
                <div className={s.select_item}>
                  <span className={s.text}>{item.title}</span>
                  <div className={s.priority}>
                    {t('priority')} |{' '}
                    {
                      freeHostsData?.freeHosts?.find(
                        (host) => host?.group?.id === item.value
                      )?.group?.priority
                    }
                  </div>
                </div>
              )}
            />

            <div
              className={cn(s.options, freeHosts.length <= 4 && s.scrolless)}
            >
              {!freeHosts.length ? (
                <div className={s.freeHostNone}>
                  {t('session.changeHost.host.none')}
                </div>
              ) : (
                freeHosts.map((host) => (
                  <div
                    key={`host-${host.alias}-${host.id}`}
                    className={cn(s['option'], {
                      [s['selected']]: selectedHostId === host.id,
                    })}
                    onClick={() => setSelectedHostId(host.id)}
                  >
                    <div className={s['left-side']}>
                      <div className={s.text}>{host.alias}</div>
                      <div className={cn(s.group_title, s.text)}>
                        {host.group.title}
                      </div>
                    </div>
                    <div className={s.priority}>
                      {t('priority')} | {host.group.priority}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </Preloader>
      </div>
      <Button
        onClick={() => {
          changeHost({
            variables: {
              id: activeSession?.id,
              input: {
                host_id: selectedHost?.id,
              },
            },
          }).catch(console.error);
        }}
        className={s.button}
        fullWidth
        disabled={isHostChanging || Boolean(!selectedHost)}
        isLoading={isHostChanging}
      >
        {t('session.changeHost.submit')}
      </Button>
    </Modal>
  );
};
